import React, { useCallback, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Button, Layout, Tooltip, Typography } from "antd";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import ArrowBack from "../assets/svg/ArrowBack.js";

import ACTIONS from "../store/action";
import { OrdersTable } from "../component/OrdersTable";
import {
  selectSearchResultCurrentPage,
  selectIsFetching,
  selectSearchedPatients,
  selectIsLoadingEnabled,
  selectViewType
} from "../store/selector";
import { ApiNames, getPharmacyPatientByIdAPI } from "../api/api";
import { decodeNumbersForSearch } from "../utils/formaters";
import styled from "styled-components";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage.js";
import { ColumnSelector } from "../component/ColumnSelector.js";
import { Routes } from "../constant/routes.js";
import { STORAGE_KEYS } from "../constant/storageKeys.js";
import { isModifierClick } from "../utils/misc.js";

const PHONE = "phone_number";
const SSN = "social_security_number";
const { Content } = Layout;
const { Text } = Typography;

const BackButtonStyle = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0;
`;

const formatSearchQuery = (searchText, searchBy) =>
  searchBy === SSN || searchBy === PHONE ? decodeNumbersForSearch(searchText) : searchText;

const SearchResultsComponent = ({ t, history, searchedPatients, searchPatientAct, setPatientModal }) => {
  const dispatch = useDispatch();
  const { search, state } = useLocation();
  const [startSearch, setStartSearch] = useState(false);
  // Store searchText specifically for SearchResults page to handle right-click "open in new tab" scenarios
  // This ensures search context is preserved when users open results in new tabs/windows
  const [localSearchText, setLocalSearchText] = useState(() =>
    localStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS_CURRENT_TEXT)
  );
  // Use state?.searchText if it exists in state (even if empty string), otherwise use localSearchText
  // This allows users to delete all characters including the last one
  const searchText = state && "searchText" in state ? state.searchText : localSearchText;

  const searchParams = new URLSearchParams(search);
  let searchBy = searchParams.get("searchBy");
  const patientId = searchParams.get("patient");
  const pharmacyPatientId = parseInt(searchParams.get("pharmacyPatient"));

  const { patients = [], total } = searchedPatients || {};

  const setPatientAct = (pharmacyPatient, patientId, orderId) =>
    dispatch(ACTIONS.setPatientAction(pharmacyPatient, patientId, orderId));
  const setSearchedPatientAct = (patient) => dispatch(ACTIONS.setSearchedPatient(patient));
  const setCurrentCycle = (currentCycle) => dispatch(ACTIONS.setCurrentCycle(currentCycle));
  const currentPage = useSelector(selectSearchResultCurrentPage);
  const isLoadingEnabled = useSelector(selectIsLoadingEnabled);
  const viewType = useSelector(selectViewType);

  useEffect(() => {
    const handleStorageChange = () => {
      const newSearchText = localStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS_CURRENT_TEXT);
      setLocalSearchText(newSearchText);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (searchBy && searchText) {
      debouncedSearch(1, searchText, searchBy);
      // Save searchText to localStorage only if it comes from state (user action)
      // This prevents overwriting with stale values and enables new tab scenarios
      if (state?.searchText) {
        localStorage.setItem(STORAGE_KEYS.SEARCH_RESULTS_CURRENT_TEXT, searchText);
        setLocalSearchText(searchText);
      }
    } else if (!searchText && !patientId) {
      // Only redirect if no patient is being viewed (prevents redirect when opening patient details)
      history.push(`${Routes.ROOT}${Routes.RECENT_PATIENTS}`);
    }
  }, [searchText, searchBy, state?.searchText]);

  const debouncedSearch = useCallback(
    debounce((page, searchText, searchBy) => {
      setStartSearch(true);
      searchPatientAct(page, formatSearchQuery(searchText, searchBy), searchBy);
    }, 500),
    []
  );

  const getPharmacyPatientById = async (pharmacyPatient) => {
    const { data } = await getPharmacyPatientByIdAPI(pharmacyPatient);
    return data;
  };

  useEffect(async () => {
    if (pharmacyPatientId && patientId) {
      setPatientModal(true, 0);
      const pharmacyPatientData = await getPharmacyPatientById(pharmacyPatientId);
      // Set pharmacyPatientCurrentOrder from the API response to ensure consistency
      pharmacyPatientData.pharmacyPatientCurrentOrder = pharmacyPatientData.currentOrder.id;
      setCurrentCycle(pharmacyPatientData);
      setPatientAct(pharmacyPatientId, patientId, pharmacyPatientData.currentOrder.id);
      setSearchedPatientAct(pharmacyPatientData);
    }
  }, [pharmacyPatientId, patientId]);

  const onClickTable = () => ({
    onCell: (pharmacyPatient) => ({
      onClick: async (e) => {
        if (isModifierClick(e)) {
          return;
        }

        try {
          const { id } = pharmacyPatient;
          setPatientModal(true, 0);
          const pharmacyPatientData = await getPharmacyPatientById(id);
          setCurrentCycle({
            ...pharmacyPatientData,
            pharmacyPatientCurrentOrder: pharmacyPatient.pharmacyPatientCurrentOrder
          });
          setPatientAct(id, pharmacyPatient.patient.id, pharmacyPatientData.currentOrder.id);
          setSearchedPatientAct(pharmacyPatientData);
        } catch (error) {
          ErrorMessage(t("errorMessages.failed_to_fetching_patient_data"));
        }
      }
    })
  });

  return (
    <Content style={{ backgroundColor: "white" }}>
      <div
        style={{
          background: "#f5f5f5",
          padding: "8px 52px",
          borderBottom: "1px solid #d4d4d5",
          marginBottom: 16
        }}
      >
        <Tooltip title={t("back")}>
          <BackButtonStyle onClick={() => history.push(Routes.ROOT + viewType)} type="text">
            <ArrowBack />
          </BackButtonStyle>
        </Tooltip>
        <div>
          <Text style={{ fontWeight: "700", color: "#525055" }}>{t("search_results")}</Text>
          <Text style={{ marginLeft: "5px", color: "#525055" }} id="patient_name_search_results">
            for {searchText}
          </Text>
        </div>
      </div>
      <div
        style={{
          paddingRight: "50px",
          width: "100%",
          display: patients.length > 0 ? "flex" : "none",
          justifyContent: "end"
        }}
      >
        <ColumnSelector />
      </div>
      <OrdersTable
        fetchOrders={(page) => debouncedSearch(page, searchText, searchBy)}
        patients={startSearch ? patients : []}
        startSearch={startSearch}
        total={total}
        onClickTable={onClickTable}
        isSearch={true}
        searchInput={""}
        currentPage={currentPage}
        isLoadingEnabled={isLoadingEnabled}
      />
    </Content>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([ApiNames.SearchFreeText]),
  searchedPatients: selectSearchedPatients
});

const mapDispatchToProps = (dispatch) => ({
  searchPatientAct: (page, SearchText, sorter, searchBy) =>
    dispatch(ACTIONS.searchPatientsAction(page, SearchText, sorter, searchBy)),
  setPatientModal: (show, tab) => dispatch(ACTIONS.setShowPatientModal(show, tab))
});

const SearchResults = compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(SearchResultsComponent);
export { SearchResults };
